<script setup lang="ts">
import { onMounted } from "vue";

import { sanitizeObject } from "@/helpers/object";

import ModalCard from "@/dsComponents/cards/ModalCard.vue";
import Overlay from "@/dsComponents/modals/Overlay.vue";

interface ModalProps {
    title?: string;
    width?: string;
    height?: string;
    minWidth?: string;
    minHeight?: string;
    maxWidth?: string;
    maxHeight?: string;
    open: boolean;
    close?: boolean;
    position?: "center" | "top";
    action?: boolean;
    noPadding?: boolean;
    centerHeader?: boolean;
    disableOverlayClose?: boolean;
    contentOverflow?: string;
    zIndexLevel?: number;
    noPaddingHeader?: boolean;
    noPaddingFooter?: boolean;
    hideScrollbar?: boolean;
}

const props = withDefaults(defineProps<ModalProps>(), {
    position: "top",
    width: "auto",
    height: "auto",
    contentOverflow: "auto",
    zIndexLevel: 0,
    maxWidth: "800px",
});

const emit = defineEmits<(name: "close", event: MouseEvent | KeyboardEvent) => void>();

const handleClose = (event: MouseEvent | KeyboardEvent) => emit("close", event);

const handleEscape = (event: KeyboardEvent) => {
    if (props.disableOverlayClose) return;
    handleClose(event);
};

const handleOverlayClick = (event: MouseEvent) => {
    if (props.disableOverlayClose) return;
    handleClose(event);
};

onMounted(() => {
    document.addEventListener("keydown", (event) => {
        if (event.key === "Escape") {
            if (props.disableOverlayClose) return;
            handleClose(event);
        }
    });
});
</script>
<template>
    <Teleport to="#teleport-modal">
        <Overlay v-show="open" @click="handleOverlayClick" :position="position" :zIndexLevel="zIndexLevel">
            <div
                :class="['ds-modal', $attrs.class]"
                :style="
                    sanitizeObject({
                        width,
                        height,
                        minWidth,
                        maxWidth: width !== 'auto' ? undefined : maxWidth,
                        maxHeight: height !== 'auto' ? undefined : maxHeight,
                    })
                "
                tabindex="-1"
                ref="modalContainer"
                @keyup.esc="handleEscape"
            >
                <ModalCard
                    :close="close"
                    :contentOverflow="contentOverflow"
                    :title="title"
                    :action="action"
                    :noPadding="noPadding"
                    :noPaddingHeader="noPaddingHeader"
                    :noPaddingFooter="noPaddingFooter"
                    :centerHeader="centerHeader"
                    :hideScrollbar="hideScrollbar"
                    @close="handleClose"
                >
                    <template #header>
                        <slot name="header"></slot>
                    </template>
                    <slot></slot>
                    <template #footer>
                        <slot name="footer"></slot>
                    </template>
                </ModalCard>
            </div>
        </Overlay>
    </Teleport>
</template>
<style lang="scss" scoped>
.ds-modal {
    position: relative;
    display: flex;
    justify-content: center;
    .close-btn {
        position: absolute;
        top: 10px;
        right: 10px;
    }
}
</style>
